import {getIsClipboardReadSupported} from '@Libraries/clipboard-library';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import type {Textbox} from '@postermywall/fabricjs-2';
import {getDefaultLineList} from '@PosterWhiteboard/items/text-item/text-line-list.library';
import {getListTypeFromHTML, getListTypeFromPastedText, LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';
import {applyListToTextboxSelection, replaceTextboxSelectionWithText} from '@PosterWhiteboard/items/text-item/text-style-runs-compiler';
import {validateText} from '@PosterWhiteboard/libraries/text.library';
import {getValidatedItemsAndPosterClipboardData} from '@PosterWhiteboard/page/page-clipboard';
import {HTML_CLIPBOARD_MIMETYPE, PLAIN_TEXT_CLIPBOARD_MIMETYPE, readFromClipboard} from '@Utils/clipboard.util';

export interface PastedText {
  text: string;
  listType: LIST_TYPES;
}

/**
 * Resolves what a paste into an editing text box should insert.
 *
 * A native paste (Cmd-V / Edit > Paste) carries its data on the event, which needs no clipboard permission and keeps
 * working when the OS or browser refuses programmatic clipboard reads (ticket 316820: paste inserted nothing in every
 * browser on one Mac). Its default action is cancelled synchronously, before any await, so fabric's own textarea
 * input never doubles the insertion. Only a paste with no event data — the right-click Paste button — has to go
 * through the async Clipboard API.
 */
export const resolvePastedText = async (event?: Event): Promise<PastedText | null> => {
  if (event instanceof ClipboardEvent && event.clipboardData) {
    event.preventDefault();
    return getPastedTextFromClipboardData(event.clipboardData);
  }
  return getPastedTextFromClipboardApi(event);
};

const getPastedTextFromClipboardData = (clipboardData: DataTransfer): PastedText | null => {
  const text = getPastableText(clipboardData.getData(PLAIN_TEXT_CLIPBOARD_MIMETYPE));
  if (!text) {
    return null;
  }
  return {text, listType: getListTypeFromHTML(clipboardData.getData(HTML_CLIPBOARD_MIMETYPE))};
};

const getPastedTextFromClipboardApi = async (event?: Event): Promise<PastedText | null> => {
  if (await getIsClipboardReadSupported()) {
    event?.preventDefault();
  }
  const text = await getTextFromClipboard();
  if (!text) {
    return null;
  }
  return {text, listType: await getListTypeFromPastedText()};
};

export const getTextFromClipboard = async (): Promise<string | undefined> => {
  let clipText;

  try {
    clipText = await readFromClipboard();
  } catch {
    return undefined;
  }

  return getPastableText(clipText);
};

/**
 * Text copied from a single PosterMyWall text item arrives as serialized item JSON and pastes as that item's text;
 * any other copied item JSON is not pastable as text.
 */
const getPastableText = (clipText: string): string | undefined => {
  try {
    const data: unknown = JSON.parse(clipText);
    if (typeof data === 'object') {
      const validateditemsAndPoster = getValidatedItemsAndPosterClipboardData(data);
      if (validateditemsAndPoster) {
        if (validateditemsAndPoster.graphicItemObjects.length === 1 && validateditemsAndPoster.graphicItemObjects[0].gitype === ITEM_TYPE.TEXT) {
          const textItemObject = validateditemsAndPoster.graphicItemObjects[0] as TextItemObject;
          return textItemObject.text;
        }
      }
      return undefined;
    }
  } catch {
    return clipText;
  }
  return clipText;
};

/**
 * Inserts a paste at the editing textbox's selection through fabric's editing API (so the
 * per-character styles shift with the text) and, for a pasted list, lists the pasted lines
 * only — the lines the paste spans, not the whole box.
 */
export const insertPastedTextIntoTextbox = (textbox: Textbox, pastedText: PastedText): void => {
  const pastedRangeStart = textbox.selectionStart;
  replaceTextboxSelectionWithText(textbox, validateText(pastedText.text));
  if (pastedText.listType !== LIST_TYPES.NONE) {
    applyListToTextboxSelection(textbox, getDefaultLineList(pastedText.listType), pastedRangeStart, textbox.selectionStart);
  }
};
